<script setup lang="ts">
import { computed, nextTick, onMounted, ref } from 'vue';
import '@ilteducation/video-player/src/scss/style.scss';
import config from '@/config/ConfigProvider';
import type { ManabuaVideoResponse, ManabuaVideosResponse } from '@ilteducation/ilt-connect-types';
import axios from 'axios';
import VideoPlayer from '@ilteducation/video-player/src/player';
import { useLocale } from '@/plugins/translation';
import useLanguagesStore from '@/store/languages';
import useAppVersion from '@/composables/use-app-version';

const videos = ref<ManabuaVideoResponse[]>([]);
const players = ref<Record<string, any>>({});

const serviceName = config.value('serviceName');
const logoImageFile = config.value('logoImageFile');
const logoImage = computed(() => new URL(`/src/assets/images/${logoImageFile}`, import.meta.url).href);
const countryCode = config.value('countryCode') as string;
const publicCollection = import.meta.env[`VITE_PUBLIC_VIDEOS_COLLECTION_${countryCode.toUpperCase()}`];

const { t, l } = useLocale();
const { getMarketLanguage } = useLanguagesStore();

const { appVersion } = useAppVersion();
const ILTVideos: Record<string, VideoPlayer> = {};

const initVideoPlayer = async (video: ManabuaVideoResponse) => {
  if (ILTVideos[video.id]) {
    ILTVideos[video.id].audioHandler.pause();
  }

  ILTVideos[video.id] = new VideoPlayer({
    userIdentity: {
      type: 'User',
      id: 'demo',
    },
    application: 'manabua',
    applicationVersion: appVersion,
    siteLanguage: getMarketLanguage(),
    videoUid: video.id,
    categoryUid: video.categoryId,
    hls: video.hlsUrl,
    trackName: video.title,
    poster: video.previewUrl,
    playbackRates: [0.5, 0.8, 1, 1.2, 1.5, 2],
    audioTracks: video.narrations.map((item) => ({
      langCode: item.languageCode,
      language: item.language,
      narratorPath: item.url,
    })),
    subtitleTracks: video.subtitles.map((item) => ({
      langCode: item.languageCode,
      language: item.language,
      subtitlePath: item.url,
    })),
    audioCaption: t(l.film.sound),
    subtitleCaption: t(l.film.subtitles),
    offCaption: t(l.film.off),
    videoJsConfig: {},
    enabledButtons: ['subtitleSize', 'fullscreen'],
    i18n: (key: string) => t(key),
  });

  players.value[video.id] = ILTVideos[video.id].init();
  nextTick(() => {
    ILTVideos[video.id].load();
  });
};

onMounted(async () => {
  videos.value = await axios<ManabuaVideosResponse>(
    `${config.value('iltConnectUrl')}/manabua/public/${publicCollection}/videos`,
    // This page is for visitors without a session, and the endpoint is public. Without `noAuth`
    // the auth interceptor cancels the request for lack of a token.
    { noAuth: true },
  ).then((r) => r.data);
  videos.value.forEach((video) => initVideoPlayer(video));
});
</script>

<template>
  <v-container>
    <v-row justify="center" class="mt-9">
      <v-col cols="6" sm="3">
        <v-img :src="logoImage" cover :alt="serviceName + ' logo'" />
      </v-col>
    </v-row>
    <v-row>
      <v-col class="text-secondary">
        <h1>{{ t(l.public_videos.title) }}</h1>
      </v-col>
    </v-row>
    <v-row class="my-3">
      <template v-if="!videos.length">
        <v-col v-for="n in 5" :key="n" cols="12" sm="6">
          <v-skeleton-loader type="text,image"></v-skeleton-loader>
        </v-col>
      </template>
      <v-col v-for="video in videos" :key="video.id" cols="12" sm="6">
        <v-card>
          <v-card-title class="film-title">
            {{ video.title }}
          </v-card-title>
          <v-card-text>
            <div class="videoContainer-inner" v-html="players[video.id]"></div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<style lang="scss" scoped>
.film-title {
  color: rgba(var(--v-theme-primary));
}

// iPhone Safari has no element Fullscreen API, so the player falls back to CSS fullscreen, which
// cannot escape a clipping or stacking ancestor - and v-card is both. Only while it is fullscreen,
// so the card keeps clipping its own rounded corners the rest of the time.
:deep(.v-card:has(.fullscreen-wrap.fullscreen)) {
  overflow: visible;
  z-index: auto;
}
</style>
